import { Entity } from 'models'
import { PaginatedResult } from 'models/pagination'

const chunkArray = <T,>(items: Array<T>, size: number) =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size),
  )

type FetchVehiclesByEntityBatchesOptions<T> = {
  chunkSize?: number
  dedupeKey?: (item: T) => string | number
  maxItems?: number
  signal?: AbortSignal
}

const createAbortError = (signal?: AbortSignal) => {
  if (signal?.reason instanceof Error) {
    return signal.reason
  }

  const abortError = new Error('Aborted')
  abortError.name = 'AbortError'
  return abortError
}

export async function fetchVehiclesByEntityBatches<T>(
  entitiesIds: Array<Entity['id']>,
  fetcher: (batch: Array<Entity['id']>, signal?: AbortSignal) => Promise<PaginatedResult<T>>,
  options?: FetchVehiclesByEntityBatchesOptions<T>,
): Promise<PaginatedResult<T>> {
  const chunkSize = options?.chunkSize ?? 15
  const dedupeKey = options?.dedupeKey
  const maxItems = options?.maxItems
  const signal = options?.signal

  const sanitizedIds = entitiesIds.filter(
    (id): id is Entity['id'] => typeof id === 'string' && id.length > 0,
  )
  if (sanitizedIds.length === 0) return { items: [], totalCount: 0 }

  const batches = chunkArray(sanitizedIds, chunkSize)
  const seenKeys = dedupeKey ? new Set<string | number>() : undefined

  const throwIfAborted = () => {
    if (signal?.aborted) {
      throw createAbortError(signal)
    }
  }

  const aggregatePromise = batches.reduce<Promise<{ items: Array<T>; totalCount: number }>>(
    async (accPromise, batch) => {
      const acc = await accPromise
      if (typeof maxItems === 'number' && acc.items.length >= maxItems) {
        return acc
      }

      throwIfAborted()

      const { items, totalCount: batchCount } = await fetcher(batch, signal)
      const nextItems = [...acc.items]

      items.forEach((item) => {
        if (dedupeKey) {
          const key = dedupeKey(item)
          if (seenKeys?.has(key)) return
          seenKeys?.add(key)
        }
        if (typeof maxItems === 'number' && nextItems.length >= maxItems) return
        nextItems.push(item)
      })

      return {
        items: nextItems,
        totalCount: acc.totalCount + batchCount,
      }
    },
    Promise.resolve({ items: [] as Array<T>, totalCount: 0 }),
  )

  throwIfAborted()
  return aggregatePromise
}
